import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  en: {
    publicAdmission: {
      language: {
        label: "Language",
        english: "English",
        hausa: "Hausa",
        arabic: "Arabic",
      },
      common: {
        yes: "Yes",
        no: "No",
        field: "Field",
        applicant: "Applicant",
        parentGuardian: "Parent/Guardian",
        schoolAdmissionPortal: "School Admission Portal",
        admissionApplication: "Admission Application",
        admissionPortal: "Admission Portal",
        admission: "Admission",
        toBeAnnounced: "To be announced",
      },
      loading: {
        openingTitle: "Opening admission portal",
        connecting: "Connecting securely...",
        pleaseWait: "Please wait...",
        checkingLink: "Checking your link",
        verifyingAccess: "Verifying admission access...",
        loadingForm: "Loading admission form",
        gettingDetails: "Getting form details ready...",
        validatingAccess: "Validating access",
        confirmingRequirements: "Confirming admission requirements...",
        validatingPin: "Validating admission pin",
        checkingPinSession: "Checking your existing pin session...",
        almostReady: "Almost ready",
        preparingForm: "Preparing your form...",
        ready: "Ready",
        formLoaded: "Admission form loaded.",
        publicAdmission: "Public Admission",
      },
      status: {
        autoSaving: "Auto-saving draft...",
        autoSavePaused: "Auto-save paused. Use Save Draft to retry.",
        draftSavedAt: "Draft saved at {{time}}",
        autoSaveEnabled: "Auto-save enabled",
        restoringDraft: "Restoring your saved application draft...",
        loadingClassOptions: "Loading class options...",
        continueWhenComplete: "You can continue once this completes.",
      },
      errors: {
        unavailableLink: "This admission form is no longer available from the selected public link.",
        unableToLoad: "Unable to load admission form.",
        noPublishedForm: "No published form available.",
        noSchoolContext: "School context could not be resolved.",
        pinExpired: "Admission pin verification expired. Please re-enter your pin.",
        required: "{{field}} is required.",
        validPhone: "Enter a valid 11-digit phone number (e.g. 08037668169).",
        validEmail: "Enter a valid email address.",
        validNumber: "Enter a valid number.",
        validDate: "Enter a valid date.",
        minLength: "Minimum {{count}} characters required.",
        maxLength: "Maximum {{count}} characters allowed.",
        minValue: "Value must be at least {{value}}.",
        maxValue: "Value must not exceed {{value}}.",
        dobFuture: "Date of birth cannot be in the future.",
        duplicatePhone: "Phone numbers must be different across the form.",
        resizePhoto: "Unable to resize the selected passport photo.",
        fileType: "This file type is not allowed for the selected field.",
        fileTooLarge: "File must be {{limit}} or less.",
        uploadFailed: "File upload failed.",
        waitUploadsSave: "Please wait for all uploads to finish before saving draft.",
        waitUploadsSubmit: "Please wait for all uploads to finish before submitting.",
        completeRequired: "Please complete all required fields and fix highlighted errors.",
        saveDraftFailed: "Unable to save draft.",
        submitFailed: "Unable to submit application.",
        fixHighlighted: "Please fix the highlighted fields:",
        noSections: "No visible sections are available for this form.",
        uploadsInProgress: "Upload in progress. Please wait before submitting.",
        requiredIncomplete: "Some required fields are still incomplete. Click Submit to see exact errors.",
        noOptions: "No options configured for this field.",
      },
      notifications: {
        draftSaved: "Draft saved. Re-enter the same admission pin to continue later.",
        submitted: "Application submitted successfully.",
      },
      unavailable: {
        title: "Online Admission Closed",
        detail:
          "Online admission application is currently closed. Please contact the school for the active application form or check back later.",
      },
      pin: {
        requiredTitle: "Admission Pin Required",
        requiredDetail: "Enter a valid admission pin to unlock this online form.",
        goToEntry: "Go to Pin Entry",
        verified: "Admission pin verified",
        activeSession: "Your pin is active for this submission session.",
        usedAfterSubmit: "It will be marked as used after successful final submission.",
        lockedField: "This field is fixed by the admission pin issued to you.",
      },
      hero: {
        eyebrow: "Online Application",
        detail: "Complete the form below and submit your application online.",
        session: "Session: {{session}}",
      },
      submitted: {
        title: "Application Submitted",
        detail: "Your application has been received. Keep this reference number for future follow-up.",
        albalagExamSoon: " The date of entrance examination will be announced soon.",
        applicationNo: "Application No.",
        applicant: "Applicant",
        class: "Class",
        usedPin: "Used Pin",
        fillAnother: "Fill Another Application",
      },
      exam: {
        eyebrow: "Aptitude / Screening Exam Card",
        readyTitle: "Exam card is ready",
        notReadyTitle: "Exam card is not ready yet",
        readyDetail: "Download and print your exam card. Present it on the screening day.",
        albalagNotReadyDetail: "The date of entrance examination will be announced soon. Please keep your application number and check back after the school announcement.",
        notReadyDetail: "The school has not set the aptitude test date for this form yet. Please check back later, or wait for the school SMS before returning to print your exam card.",
        screeningDate: "Screening Date",
        screeningTime: "Screening Time",
        noteLabel: "Instructions / venue note",
        preparing: "Preparing Exam Card...",
        download: "Download / Print Exam Card",
        nextLabel: "What to do next",
        nextAlbalag: "Keep your application number. The date of entrance examination will be announced soon.",
        nextDefault: "Keep your application number. Return to this portal after the school announces the aptitude test date.",
      },
      acknowledgement: {
        preparing: "Preparing Acknowledgement...",
        download: "Download Acknowledgement Form",
      },
      guide: {
        eyebrow: "Before You Start",
        title: "Read the application guide",
        detail: "Complete the steps below first, then start the online application.",
        estimatedTimeTitle: "Estimated Time",
        estimatedTimeDetail: "Most parents complete this form in 8-15 minutes.",
        mobileTitle: "Mobile Friendly",
        mobileDetail: "Use your phone comfortably. You can continue section by section.",
        documentsTitle: "Required Documents",
        documentsDetail: "Passport photos are resized automatically. Other document uploads max 1MB.",
        reviewTitle: "Review Before Submit",
        reviewDetail: "Confirm phone numbers and required fields to avoid delays.",
        checklistTitle: "Quick Checklist",
        checklistPhone: "Use 11-digit Nigerian phone numbers (e.g. 08037668169).",
        checklistRequired: "Ensure all compulsory fields are completed before submit.",
        checklistPassport: "Passport photos can be taken with camera or selected from files.",
        checklistApplicationNo: "Keep your application number after final submission.",
        checklistDraft: "You can save draft and continue later.",
      },
      progress: {
        title: "Application Progress",
        completed: "{{percent}}% completed",
        sectionOf: "Section {{current}} of {{total}}",
        complete: "Complete",
        step: "Step {{step}}",
        required: "Required:",
      },
      actions: {
        continueApplication: "Continue Application",
        startApplication: "Start Application",
        previous: "Previous",
        next: "Next",
        savingDraft: "Saving Draft...",
        updateDraft: "Update Draft",
        saveDraft: "Save Draft",
        submitting: "Submitting...",
        submitApplication: "Submit Application",
        continue: "Continue",
      },
      upload: {
        replace: "Replace Upload",
        fileUpload: "File Upload",
        passportPreview: "Passport preview",
        uploadingPhoto: "Uploading resized photo...",
        photoReady: "Photo ready.",
        currentFile: "Current file uploaded",
        viewFile: "View uploaded file",
        remove: "Remove",
        browsePassport: "Browse Passport",
        chooseFile: "Choose File",
        useCamera: "Use Camera",
        uploading: "Uploading...",
        passportResizeNote: "Passport photos are resized before upload.",
        maxSize: "Max {{size}}",
        replaceNote: "Select another file above to replace current upload",
      },
      input: {
        selectOption: "Select an option",
        agree: "I agree",
      },
      meta: {
        description: "Complete the admission application for {{schoolName}}.",
      },
    },
  },
  ha: {
    publicAdmission: {
      language: {
        label: "Harshe",
        english: "Turanci",
        hausa: "Hausa",
        arabic: "Larabci",
      },
      common: {
        yes: "Eh",
        no: "A'a",
        field: "Filin bayanai",
        applicant: "Mai nema",
        parentGuardian: "Iyaye/Mai kula",
        schoolAdmissionPortal: "Tashar Shiga Makaranta",
        admissionApplication: "Takardar Neman Shiga",
        admissionPortal: "Tashar Admission",
        admission: "Admission",
        toBeAnnounced: "Za a sanar",
      },
      loading: {
        openingTitle: "Ana bude tashar admission",
        connecting: "Ana hadawa cikin tsaro...",
        pleaseWait: "Daure ka jira...",
        checkingLink: "Ana duba link din ka",
        verifyingAccess: "Ana tabbatar da izinin shiga...",
        loadingForm: "Ana lodin form din admission",
        gettingDetails: "Ana shirya bayanan form...",
        validatingAccess: "Ana tabbatar da izini",
        confirmingRequirements: "Ana tabbatar da bukatun admission...",
        validatingPin: "Ana tabbatar da pin din admission",
        checkingPinSession: "Ana duba zaman pin din ka...",
        almostReady: "Ya kusa gama",
        preparingForm: "Ana shirya form din ka...",
        ready: "Ya shirya",
        formLoaded: "An loda form din admission.",
        publicAdmission: "Admission na Jama'a",
      },
      status: {
        autoSaving: "Ana ajiye draft...",
        autoSavePaused: "An dakatar da auto-save. Yi amfani da Save Draft don sake gwadawa.",
        draftSavedAt: "An ajiye draft da {{time}}",
        autoSaveEnabled: "Auto-save na aiki",
        restoringDraft: "Ana dawo da draft din application dinka...",
        loadingClassOptions: "Ana loda zabin aji...",
        continueWhenComplete: "Za ka iya ci gaba idan ya kammala.",
      },
      errors: {
        unavailableLink: "Wannan form din admission baya samuwa daga wannan public link.",
        unableToLoad: "An kasa loda form din admission.",
        noPublishedForm: "Babu published form da ake samu.",
        noSchoolContext: "An kasa gane bayanan makaranta.",
        pinExpired: "Tabbacin pin din admission ya kare. Sake shigar da pin din ka.",
        required: "Ana bukatar {{field}}.",
        validPhone: "Shigar da lambar waya mai lambobi 11 (misali 08037668169).",
        validEmail: "Shigar da email mai inganci.",
        validNumber: "Shigar da lamba mai inganci.",
        validDate: "Shigar da kwanan wata mai inganci.",
        minLength: "Ana bukatar akalla haruffa {{count}}.",
        maxLength: "Ba za a wuce haruffa {{count}} ba.",
        minValue: "Dole darajar ta kasance akalla {{value}}.",
        maxValue: "Darajar ba za ta wuce {{value}} ba.",
        dobFuture: "Ranar haihuwa ba za ta kasance a gaba ba.",
        duplicatePhone: "Dole lambobin waya su bambanta a cikin form.",
        resizePhoto: "An kasa rage girman passport photo din da aka zaba.",
        fileType: "Ba a yarda da irin wannan file ba a wannan fili.",
        fileTooLarge: "File dole ya kasance {{limit}} ko kasa.",
        uploadFailed: "Upload din file ya kasa.",
        waitUploadsSave: "Daure ka jira duk uploads su gama kafin ajiye draft.",
        waitUploadsSubmit: "Daure ka jira duk uploads su gama kafin submitting.",
        completeRequired: "Cika duk filayen da ake bukata kuma gyara kurakuran da aka nuna.",
        saveDraftFailed: "An kasa ajiye draft.",
        submitFailed: "An kasa submit application.",
        fixHighlighted: "Daure ka gyara filayen da aka nuna:",
        noSections: "Babu sassan da ake iya gani a wannan form.",
        uploadsInProgress: "Upload na gudana. Daure ka jira kafin submitting.",
        requiredIncomplete: "Wasu filayen da ake bukata ba su cika ba. Danna Submit don ganin kurakurai.",
        noOptions: "Babu zabuka da aka saita a wannan fili.",
      },
      notifications: {
        draftSaved: "An ajiye draft. Sake shigar da wannan admission pin don ci gaba daga baya.",
        submitted: "An submit application cikin nasara.",
      },
      unavailable: {
        title: "An Rufe Online Admission",
        detail:
          "Online admission application a yanzu a rufe yake. Daure ka tuntubi makaranta don samun form din da yake aiki ko ka dawo daga baya.",
      },
      pin: {
        requiredTitle: "Ana Bukatar Admission Pin",
        requiredDetail: "Shigar da admission pin mai inganci don bude wannan online form.",
        goToEntry: "Je zuwa Shigar da Pin",
        verified: "An tabbatar da admission pin",
        activeSession: "Pin din ka yana aiki a wannan zaman submission.",
        usedAfterSubmit: "Za a nuna an yi amfani da shi bayan final submission ya yi nasara.",
        lockedField: "Wannan fili an kulle shi bisa admission pin da aka baka.",
      },
      hero: {
        eyebrow: "Online Application",
        detail: "Cika form din da ke kasa sannan ka submit application dinka online.",
        session: "Session: {{session}}",
      },
      submitted: {
        title: "An Submit Application",
        detail: "An karbi application dinka. Ka ajiye wannan reference number don bibiya.",
        albalagExamSoon: " Za a sanar da ranar jarabawar shiga nan gaba.",
        applicationNo: "Application No.",
        applicant: "Mai nema",
        class: "Aji",
        usedPin: "Pin Da Aka Yi Amfani Da Shi",
        fillAnother: "Cika Wani Application",
      },
      exam: {
        eyebrow: "Katin Aptitude / Screening Exam",
        readyTitle: "Exam card ya shirya",
        notReadyTitle: "Exam card bai shirya ba tukuna",
        readyDetail: "Sauke kuma buga exam card dinka. Ka gabatar da shi ranar screening.",
        albalagNotReadyDetail: "Za a sanar da ranar jarabawar shiga nan gaba. Ka ajiye application number dinka kuma ka sake dubawa bayan sanarwar makaranta.",
        notReadyDetail: "Makaranta bata saita ranar aptitude test ga wannan form ba tukuna. Ka sake dubawa daga baya, ko ka jira SMS daga makaranta kafin dawowa don buga exam card.",
        screeningDate: "Ranar Screening",
        screeningTime: "Lokacin Screening",
        noteLabel: "Umarni / bayanin wuri",
        preparing: "Ana Shirya Exam Card...",
        download: "Sauke / Buga Exam Card",
        nextLabel: "Abin da za a yi gaba",
        nextAlbalag: "Ka ajiye application number dinka. Za a sanar da ranar jarabawar shiga nan gaba.",
        nextDefault: "Ka ajiye application number dinka. Ka dawo wannan portal bayan makaranta ta sanar da ranar aptitude test.",
      },
      acknowledgement: {
        preparing: "Ana Shirya Acknowledgement...",
        download: "Sauke Acknowledgement Form",
      },
      guide: {
        eyebrow: "Kafin Ka Fara",
        title: "Karanta jagorar application",
        detail: "Kammala matakan da ke kasa tukuna, sannan ka fara online application.",
        estimatedTimeTitle: "Lokacin Da Zai Dauka",
        estimatedTimeDetail: "Yawancin iyaye suna cika wannan form cikin minti 8-15.",
        mobileTitle: "Ya Dace Da Waya",
        mobileDetail: "Yi amfani da wayarka cikin sauki. Za ka iya ci gaba sashe bayan sashe.",
        documentsTitle: "Takardun Da Ake Bukata",
        documentsDetail: "Ana rage girman passport photos kai tsaye. Sauran uploads max 1MB.",
        reviewTitle: "Duba Kafin Submit",
        reviewDetail: "Tabbatar da lambobin waya da filayen da ake bukata don kauce wa jinkiri.",
        checklistTitle: "Jerin Dubawa",
        checklistPhone: "Yi amfani da lambar waya ta Najeriya mai lambobi 11 (misali 08037668169).",
        checklistRequired: "Tabbatar an cika duk filayen dole kafin submit.",
        checklistPassport: "Za a iya daukar passport photo da camera ko zaba daga files.",
        checklistApplicationNo: "Ka ajiye application number bayan final submission.",
        checklistDraft: "Za ka iya ajiye draft ka ci gaba daga baya.",
      },
      progress: {
        title: "Ci Gaban Application",
        completed: "{{percent}}% an kammala",
        sectionOf: "Sashe {{current}} cikin {{total}}",
        complete: "An kammala",
        step: "Mataki {{step}}",
        required: "Ana bukata:",
      },
      actions: {
        continueApplication: "Ci Gaba Da Application",
        startApplication: "Fara Application",
        previous: "Baya",
        next: "Gaba",
        savingDraft: "Ana Ajiye Draft...",
        updateDraft: "Sabunta Draft",
        saveDraft: "Ajiye Draft",
        submitting: "Ana Submit...",
        submitApplication: "Submit Application",
        continue: "Ci gaba",
      },
      upload: {
        replace: "Sauya Upload",
        fileUpload: "Upload File",
        passportPreview: "Duban passport",
        uploadingPhoto: "Ana uploading resized photo...",
        photoReady: "Photo ya shirya.",
        currentFile: "File na yanzu ya uploaded",
        viewFile: "Duba file din da aka upload",
        remove: "Cire",
        browsePassport: "Zabi Passport",
        chooseFile: "Zabi File",
        useCamera: "Yi Amfani Da Camera",
        uploading: "Ana upload...",
        passportResizeNote: "Ana rage girman passport photos kafin upload.",
        maxSize: "Max {{size}}",
        replaceNote: "Zabi wani file a sama don sauya upload na yanzu",
      },
      input: {
        selectOption: "Zabi daya",
        agree: "Na yarda",
      },
      meta: {
        description: "Cika application din admission na {{schoolName}}.",
      },
    },
  },
  ar: {
    publicAdmission: {
      language: {
        label: "اللغة",
        english: "الإنجليزية",
        hausa: "الهوسا",
        arabic: "العربية",
      },
      common: {
        yes: "نعم",
        no: "لا",
        field: "الحقل",
        applicant: "المتقدم",
        parentGuardian: "ولي الأمر",
        schoolAdmissionPortal: "بوابة القبول المدرسي",
        admissionApplication: "طلب القبول",
        admissionPortal: "بوابة القبول",
        admission: "القبول",
        toBeAnnounced: "سيتم الإعلان لاحقا",
      },
      loading: {
        openingTitle: "فتح بوابة القبول",
        connecting: "جار الاتصال الآمن...",
        pleaseWait: "يرجى الانتظار...",
        checkingLink: "جار فحص الرابط",
        verifyingAccess: "جار التحقق من صلاحية الدخول...",
        loadingForm: "تحميل نموذج القبول",
        gettingDetails: "تجهيز تفاصيل النموذج...",
        validatingAccess: "التحقق من الدخول",
        confirmingRequirements: "تأكيد متطلبات القبول...",
        validatingPin: "التحقق من رقم القبول",
        checkingPinSession: "فحص جلسة رقم القبول الحالية...",
        almostReady: "أوشكنا على الانتهاء",
        preparingForm: "تجهيز النموذج...",
        ready: "جاهز",
        formLoaded: "تم تحميل نموذج القبول.",
        publicAdmission: "قبول عام",
      },
      status: {
        autoSaving: "جار حفظ المسودة...",
        autoSavePaused: "توقف الحفظ التلقائي. استخدم حفظ المسودة للمحاولة مرة أخرى.",
        draftSavedAt: "تم حفظ المسودة في {{time}}",
        autoSaveEnabled: "الحفظ التلقائي مفعل",
        restoringDraft: "استعادة مسودة الطلب المحفوظة...",
        loadingClassOptions: "تحميل خيارات الصف...",
        continueWhenComplete: "يمكنك المتابعة بعد اكتمال ذلك.",
      },
      errors: {
        unavailableLink: "لم يعد نموذج القبول هذا متاحا من الرابط العام المحدد.",
        unableToLoad: "تعذر تحميل نموذج القبول.",
        noPublishedForm: "لا يوجد نموذج منشور متاح.",
        noSchoolContext: "تعذر تحديد بيانات المدرسة.",
        pinExpired: "انتهى التحقق من رقم القبول. يرجى إدخاله مرة أخرى.",
        required: "{{field}} مطلوب.",
        validPhone: "أدخل رقم هاتف صحيحا من 11 رقما (مثال 08037668169).",
        validEmail: "أدخل بريدا إلكترونيا صحيحا.",
        validNumber: "أدخل رقما صحيحا.",
        validDate: "أدخل تاريخا صحيحا.",
        minLength: "مطلوب {{count}} أحرف على الأقل.",
        maxLength: "الحد الأقصى {{count}} حرفا.",
        minValue: "يجب ألا تقل القيمة عن {{value}}.",
        maxValue: "يجب ألا تتجاوز القيمة {{value}}.",
        dobFuture: "لا يمكن أن يكون تاريخ الميلاد في المستقبل.",
        duplicatePhone: "يجب أن تكون أرقام الهاتف مختلفة في النموذج.",
        resizePhoto: "تعذر تغيير حجم صورة الجواز المختارة.",
        fileType: "نوع الملف غير مسموح لهذا الحقل.",
        fileTooLarge: "يجب ألا يزيد الملف عن {{limit}}.",
        uploadFailed: "فشل رفع الملف.",
        waitUploadsSave: "يرجى انتظار اكتمال كل الرفوعات قبل حفظ المسودة.",
        waitUploadsSubmit: "يرجى انتظار اكتمال كل الرفوعات قبل الإرسال.",
        completeRequired: "يرجى إكمال كل الحقول المطلوبة وتصحيح الأخطاء المحددة.",
        saveDraftFailed: "تعذر حفظ المسودة.",
        submitFailed: "تعذر إرسال الطلب.",
        fixHighlighted: "يرجى تصحيح الحقول المحددة:",
        noSections: "لا توجد أقسام ظاهرة لهذا النموذج.",
        uploadsInProgress: "يوجد رفع قيد التنفيذ. يرجى الانتظار قبل الإرسال.",
        requiredIncomplete: "بعض الحقول المطلوبة لم تكتمل. اضغط إرسال لرؤية الأخطاء.",
        noOptions: "لم يتم إعداد خيارات لهذا الحقل.",
      },
      notifications: {
        draftSaved: "تم حفظ المسودة. أدخل نفس رقم القبول للمتابعة لاحقا.",
        submitted: "تم إرسال الطلب بنجاح.",
      },
      unavailable: {
        title: "التقديم الإلكتروني مغلق",
        detail:
          "التقديم الإلكتروني للقبول مغلق حاليا. يرجى التواصل مع المدرسة لمعرفة نموذج التقديم النشط أو المحاولة لاحقا.",
      },
      pin: {
        requiredTitle: "رقم القبول مطلوب",
        requiredDetail: "أدخل رقم قبول صحيحا لفتح هذا النموذج الإلكتروني.",
        goToEntry: "الذهاب إلى إدخال الرقم",
        verified: "تم التحقق من رقم القبول",
        activeSession: "رقمك مفعل لجلسة الإرسال هذه.",
        usedAfterSubmit: "سيتم اعتباره مستخدما بعد نجاح الإرسال النهائي.",
        lockedField: "هذا الحقل مثبت حسب رقم القبول الصادر لك.",
      },
      hero: {
        eyebrow: "طلب إلكتروني",
        detail: "أكمل النموذج أدناه وأرسل طلبك إلكترونيا.",
        session: "الجلسة: {{session}}",
      },
      submitted: {
        title: "تم إرسال الطلب",
        detail: "تم استلام طلبك. احتفظ بهذا الرقم المرجعي للمتابعة.",
        albalagExamSoon: " سيتم إعلان تاريخ امتحان الدخول قريبا.",
        applicationNo: "رقم الطلب",
        applicant: "المتقدم",
        class: "الصف",
        usedPin: "الرقم المستخدم",
        fillAnother: "تعبئة طلب آخر",
      },
      exam: {
        eyebrow: "بطاقة اختبار القدرات / الفرز",
        readyTitle: "بطاقة الامتحان جاهزة",
        notReadyTitle: "بطاقة الامتحان غير جاهزة بعد",
        readyDetail: "قم بتنزيل وطباعة بطاقة الامتحان، وقدمها في يوم الفرز.",
        albalagNotReadyDetail: "سيتم إعلان تاريخ امتحان الدخول قريبا. يرجى الاحتفاظ برقم الطلب والعودة بعد إعلان المدرسة.",
        notReadyDetail: "لم تحدد المدرسة تاريخ اختبار القدرات لهذا النموذج بعد. يرجى العودة لاحقا أو انتظار رسالة المدرسة قبل طباعة بطاقة الامتحان.",
        screeningDate: "تاريخ الفرز",
        screeningTime: "وقت الفرز",
        noteLabel: "تعليمات / ملاحظة المكان",
        preparing: "تجهيز بطاقة الامتحان...",
        download: "تنزيل / طباعة بطاقة الامتحان",
        nextLabel: "الخطوة التالية",
        nextAlbalag: "احتفظ برقم طلبك. سيتم إعلان تاريخ امتحان الدخول قريبا.",
        nextDefault: "احتفظ برقم طلبك. عد إلى هذه البوابة بعد إعلان المدرسة لتاريخ اختبار القدرات.",
      },
      acknowledgement: {
        preparing: "تجهيز نموذج الإقرار...",
        download: "تنزيل نموذج الإقرار",
      },
      guide: {
        eyebrow: "قبل أن تبدأ",
        title: "اقرأ دليل الطلب",
        detail: "أكمل الخطوات أدناه أولا، ثم ابدأ الطلب الإلكتروني.",
        estimatedTimeTitle: "الوقت المتوقع",
        estimatedTimeDetail: "يكمل معظم أولياء الأمور هذا النموذج خلال 8-15 دقيقة.",
        mobileTitle: "مناسب للهاتف",
        mobileDetail: "استخدم هاتفك بسهولة. يمكنك المتابعة قسما بعد قسم.",
        documentsTitle: "المستندات المطلوبة",
        documentsDetail: "يتم تصغير صور الجواز تلقائيا. الحد الأقصى لبقية الملفات 1MB.",
        reviewTitle: "راجع قبل الإرسال",
        reviewDetail: "تأكد من أرقام الهاتف والحقول المطلوبة لتجنب التأخير.",
        checklistTitle: "قائمة سريعة",
        checklistPhone: "استخدم أرقام هاتف نيجيرية من 11 رقما (مثال 08037668169).",
        checklistRequired: "تأكد من إكمال كل الحقول الإلزامية قبل الإرسال.",
        checklistPassport: "يمكن التقاط صور الجواز بالكاميرا أو اختيارها من الملفات.",
        checklistApplicationNo: "احتفظ برقم طلبك بعد الإرسال النهائي.",
        checklistDraft: "يمكنك حفظ المسودة والمتابعة لاحقا.",
      },
      progress: {
        title: "تقدم الطلب",
        completed: "اكتمل {{percent}}%",
        sectionOf: "القسم {{current}} من {{total}}",
        complete: "مكتمل",
        step: "خطوة {{step}}",
        required: "المطلوب:",
      },
      actions: {
        continueApplication: "متابعة الطلب",
        startApplication: "بدء الطلب",
        previous: "السابق",
        next: "التالي",
        savingDraft: "حفظ المسودة...",
        updateDraft: "تحديث المسودة",
        saveDraft: "حفظ المسودة",
        submitting: "جار الإرسال...",
        submitApplication: "إرسال الطلب",
        continue: "متابعة",
      },
      upload: {
        replace: "استبدال الملف",
        fileUpload: "رفع ملف",
        passportPreview: "معاينة صورة الجواز",
        uploadingPhoto: "رفع الصورة بعد تصغيرها...",
        photoReady: "الصورة جاهزة.",
        currentFile: "تم رفع الملف الحالي",
        viewFile: "عرض الملف المرفوع",
        remove: "إزالة",
        browsePassport: "اختيار صورة الجواز",
        chooseFile: "اختيار ملف",
        useCamera: "استخدام الكاميرا",
        uploading: "جار الرفع...",
        passportResizeNote: "يتم تصغير صور الجواز قبل الرفع.",
        maxSize: "الحد {{size}}",
        replaceNote: "اختر ملفا آخر أعلاه لاستبدال الملف الحالي",
      },
      input: {
        selectOption: "اختر خيارا",
        agree: "أوافق",
      },
      meta: {
        description: "أكمل طلب القبول لـ {{schoolName}}.",
      },
    },
  },
};

Object.assign(resources.en.publicAdmission.language, {
  french: "French",
  igbo: "Igbo",
  yoruba: "Yoruba",
});

Object.assign(resources.ha.publicAdmission.language, {
  french: "Faransanci",
  igbo: "Igbo",
  yoruba: "Yoruba",
});

Object.assign(resources.ar.publicAdmission.language, {
  french: "الفرنسية",
  igbo: "الإيغبو",
  yoruba: "اليوروبا",
});

Object.assign(resources.en.publicAdmission, {
  codeEntry: {
    pageLabel: "Admission Pin",
    metaDescription: "Enter your admission pin to continue your {{formTitle}} at {{schoolName}}.",
    onlineAdmission: "Online Admission",
    headerDetail: "Enter your admission pin to continue your application.",
    pinFormatNote: "Pin format is numeric only and auto-validates once complete.",
    admissionPin: "Admission Pin",
    formatExample: "Format: 001-679-818-188",
    digitsCount: "{{count}}/12 digits",
    validating: "Validating...",
    validatePin: "Validate Pin",
    verifiedPin: "Verified Pin: {{code}}",
    existingFound: "Existing application found",
    existingStatus: "with status",
    activeDraft: "An active in-progress application was found for this pin.",
    downloadOfferLetter: "Download Admission / Offer Letter",
    preparingPdf: "Preparing PDF...",
    downloadExamCard: "Download Exam Card",
    preparingExamCard: "Preparing Exam Card...",
    continueApplication: "Continue Application",
    fullPin: "Please enter your full 12-digit admission pin.",
    completePinBeforeValidate: "Please complete the full pin before validating.",
    verifiedRedirect: "Admission pin verified. Redirecting to your application...",
    pinVerified: "Admission pin verified.",
    admittedPrintLetter: "Pin verified for an admitted applicant. You can now print the admission letter.",
    admittedRecordFound: "Admitted record found for this pin.",
    linkedStatus: "This pin is linked to an existing application with status: {{status}}.",
    existingApplicationFound: "Existing application found for this pin.",
    contextUnavailable: "Unable to open admission pin page.",
    noPublishedForm: "No published admission form is currently available for this school.",
    pinDifferentForm: "This pin was generated for a different admission form. Enter the pin created for this form.",
    pinAlreadyLinked: "That pin is already linked to a submitted application. Re-enter it to view the current admission outcome.",
    pinBlocked: "That pin is blocked. Contact the school for assistance.",
    invalidPin: "Invalid admission pin. Please verify and try again.",
    blockedPin: "This admission pin is currently blocked.",
    usedPin: "This admission pin has already been used.",
    unableValidate: "Unable to validate pin right now.",
  },
  albalagIntro: {
    eyebrow: "Online Admission Information",
    title: "AL-BALAG ANGLO ARABIC ACADEMY JOS",
    readBeforePin: "Please read this information before entering your admission PIN.",
    greeting: "Assalamu Alaikum wa Rahmatullahi wa Barakatuh.",
    introOne: "Thank you for your interest in enrolling your child at Albalag Anglo-Arabic Academy, Jos. The school provides boarding education that combines Qur'an memorisation, Arabic language, Islamic studies, modern academic sciences, vocational skills and information technology.",
    introTwo: "Please check the school prospectus for more information before completing the application.",
    availableClassTitle: "Available Class",
    availableClassValue: "JSS 1 male boarding only",
    availableClassNote: "Successful applicants will be admitted into Junior Secondary School One boarding.",
    interviewTitle: "Interview Focus",
    interviewValue: "I.R.K, sciences, Qur'an, English, mathematics, I.T. and Arabic",
    interviewNote: "Reading, memorisation and core academic readiness may be assessed during the interview.",
    languageTitle: "Language Options",
    languageValue: "Arabic, English and French",
    languageNote: "Arabic is the primary language of communication, with English widely used and basic French instruction.",
    feesTitle: "School Fees Summary",
    feeFirstTerm: "1st Term mandatory fees",
    feeSecondTerm: "2nd Term mandatory fees",
    feeThirdTerm: "3rd Term mandatory fees",
    feeRefundable: "Refundable fee each term",
    feedingNote: "Feeding fees may be reviewed from term to term due to food price changes. Fees should be paid at least one week before resumption.",
    documentsTitle: "Documents Required",
    birthCertificate: "Birth certificate",
    transcript: "Academic transcript / previous result",
    medicalCertificate: "Medical certificate",
    acknowledgementLetter: "Signed letter of acknowledgement",
    rulesTitle: "Important Rules",
    ruleLanguage: "Arabic is the primary language of communication within the school. English is also widely used, while students receive basic instruction in French.",
    ruleOfficialInfo: "Parents and guardians should obtain all official information directly from the school management.",
    ruleNoStaffArrangements: "No parent or guardian shall assign duties or make personal arrangements with any staff member except those officially authorised by the school.",
    rulePayments: "All financial transactions must be conducted through the school's official bank account.",
    ruleCommunication: "All communication between parents and the school shall be through the official school telephone number or other approved communication channels.",
    ruleProhibitedItems: "Bringing food provisions or other unauthorised items into the school is strictly prohibited. Any prohibited item found in a student's possession shall be confiscated.",
    ruleVisits: "Unscheduled student visits are not permitted. All visits must be arranged and approved in advance.",
    ruleStayInSchool: "Students are required to remain within the school premises throughout the school term except with official permission.",
    ruleAttendance: "Attendance and participation in all academic, religious, and extracurricular activities are compulsory.",
    ruleBehaviour: "Students are expected to maintain exemplary behaviour and uphold the values and discipline of the school at all times.",
    rulePromotion: "A student must obtain a minimum overall score of 50% to qualify for promotion to the next class. A student below 50% may repeat the current class according to the school regulations and Academic Board decision.",
    declarationTitle: "Parent / Guardian Declaration",
    declarationText: "By continuing, the parent or guardian confirms that the admission information has been read, that all supplied information will be true, and that the applicant will comply with the rules, regulations and policies of Albalag Anglo-Arabic Academy.",
    continueNote: "Continue to validate your admission PIN.",
    continueButton: "I have read this, continue to PIN",
  },
});

Object.assign(resources.ar.publicAdmission, {
  codeEntry: {
    pageLabel: "رقم القبول",
    metaDescription: "أدخل رقم القبول لمتابعة {{formTitle}} في {{schoolName}}.",
    onlineAdmission: "القبول الإلكتروني",
    headerDetail: "أدخل رقم القبول لمتابعة طلبك.",
    pinFormatNote: "صيغة الرقم أرقام فقط ويتم التحقق تلقائيا عند اكتماله.",
    admissionPin: "رقم القبول",
    formatExample: "الصيغة: 001-679-818-188",
    digitsCount: "{{count}}/12 رقما",
    validating: "جار التحقق...",
    validatePin: "تحقق من الرقم",
    verifiedPin: "الرقم المؤكد: {{code}}",
    existingFound: "تم العثور على طلب سابق",
    existingStatus: "بالحالة",
    activeDraft: "تم العثور على طلب قيد التنفيذ لهذا الرقم.",
    downloadOfferLetter: "تنزيل خطاب القبول / العرض",
    preparingPdf: "تجهيز PDF...",
    downloadExamCard: "تنزيل بطاقة الامتحان",
    preparingExamCard: "تجهيز بطاقة الامتحان...",
    continueApplication: "متابعة الطلب",
    fullPin: "يرجى إدخال رقم القبول كاملا من 12 رقما.",
    completePinBeforeValidate: "يرجى إكمال الرقم قبل التحقق.",
    verifiedRedirect: "تم التحقق من رقم القبول. جار التحويل إلى طلبك...",
    pinVerified: "تم التحقق من رقم القبول.",
    admittedPrintLetter: "تم التحقق من الرقم لمتقدم مقبول. يمكنك الآن طباعة خطاب القبول.",
    admittedRecordFound: "تم العثور على سجل قبول لهذا الرقم.",
    linkedStatus: "هذا الرقم مرتبط بطلب سابق حالته: {{status}}.",
    existingApplicationFound: "تم العثور على طلب سابق لهذا الرقم.",
    contextUnavailable: "تعذر فتح صفحة رقم القبول.",
    noPublishedForm: "لا يوجد نموذج قبول منشور حاليا لهذه المدرسة.",
    pinDifferentForm: "تم إنشاء هذا الرقم لنموذج قبول مختلف. أدخل الرقم الخاص بهذا النموذج.",
    pinAlreadyLinked: "هذا الرقم مرتبط مسبقا بطلب مرسل. أعد إدخاله لعرض نتيجة القبول الحالية.",
    pinBlocked: "هذا الرقم محظور. تواصل مع المدرسة للمساعدة.",
    invalidPin: "رقم القبول غير صحيح. يرجى التحقق والمحاولة مرة أخرى.",
    blockedPin: "رقم القبول هذا محظور حاليا.",
    usedPin: "تم استخدام رقم القبول هذا مسبقا.",
    unableValidate: "تعذر التحقق من الرقم الآن.",
  },
  albalagIntro: {
    eyebrow: "معلومات القبول الإلكتروني",
    title: "أكاديمية البلاغ الأنجلو عربية جوس",
    readBeforePin: "يرجى قراءة هذه المعلومات قبل إدخال رقم القبول.",
    greeting: "السلام عليكم ورحمة الله وبركاته.",
    introOne: "نشكركم على اهتمامكم بتسجيل ابنكم في أكاديمية البلاغ الأنجلو عربية، جوس. تقدم المدرسة تعليما داخليا يجمع بين حفظ القرآن، واللغة العربية، والدراسات الإسلامية، والعلوم الأكاديمية الحديثة، والمهارات المهنية، وتقنية المعلومات.",
    introTwo: "يرجى مراجعة نشرة المدرسة لمزيد من المعلومات قبل إكمال الطلب.",
    availableClassTitle: "الصف المتاح",
    availableClassValue: "JSS 1 للطلاب الذكور داخلي فقط",
    availableClassNote: "سيتم قبول المتقدمين الناجحين في الصف الأول الإعدادي الداخلي.",
    interviewTitle: "محاور المقابلة",
    interviewValue: "الدراسات الإسلامية، العلوم، القرآن، الإنجليزية، الرياضيات، تقنية المعلومات والعربية",
    interviewNote: "قد يتم تقييم القراءة والحفظ والاستعداد الأكاديمي الأساسي أثناء المقابلة.",
    languageTitle: "خيارات اللغة",
    languageValue: "العربية والإنجليزية والفرنسية",
    languageNote: "العربية هي اللغة الأساسية للتواصل، مع استخدام واسع للإنجليزية وتعليم أساسي للفرنسية.",
    feesTitle: "ملخص الرسوم المدرسية",
    feeFirstTerm: "رسوم الفصل الأول الإلزامية",
    feeSecondTerm: "رسوم الفصل الثاني الإلزامية",
    feeThirdTerm: "رسوم الفصل الثالث الإلزامية",
    feeRefundable: "رسوم قابلة للاسترداد كل فصل",
    feedingNote: "قد تتم مراجعة رسوم التغذية من فصل لآخر بسبب تغير أسعار الطعام. يجب دفع الرسوم قبل الاستئناف بأسبوع واحد على الأقل.",
    documentsTitle: "المستندات المطلوبة",
    birthCertificate: "شهادة الميلاد",
    transcript: "السجل الأكاديمي / النتيجة السابقة",
    medicalCertificate: "شهادة طبية",
    acknowledgementLetter: "خطاب الإقرار الموقع",
    rulesTitle: "قواعد مهمة",
    ruleLanguage: "العربية هي اللغة الأساسية للتواصل داخل المدرسة. تستخدم الإنجليزية على نطاق واسع، ويتلقى الطلاب تعليما أساسيا في الفرنسية.",
    ruleOfficialInfo: "يجب على أولياء الأمور الحصول على كل المعلومات الرسمية مباشرة من إدارة المدرسة.",
    ruleNoStaffArrangements: "لا يجوز لأي ولي أمر تكليف أي موظف بمهام أو ترتيبات شخصية إلا من خلال المخولين رسميا من المدرسة.",
    rulePayments: "يجب إجراء كل المعاملات المالية عبر الحساب البنكي الرسمي للمدرسة.",
    ruleCommunication: "يجب أن يكون التواصل بين أولياء الأمور والمدرسة عبر رقم الهاتف الرسمي أو القنوات المعتمدة.",
    ruleProhibitedItems: "إحضار المواد الغذائية أو غيرها من المواد غير المصرح بها إلى المدرسة ممنوع تماما. وستتم مصادرة أي مادة ممنوعة توجد مع الطالب.",
    ruleVisits: "الزيارات غير المجدولة للطلاب غير مسموحة. يجب ترتيب كل الزيارات والموافقة عليها مسبقا.",
    ruleStayInSchool: "يجب على الطلاب البقاء داخل حرم المدرسة طوال الفصل الدراسي إلا بإذن رسمي.",
    ruleAttendance: "الحضور والمشاركة في كل الأنشطة الأكاديمية والدينية واللامنهجية إلزامية.",
    ruleBehaviour: "يتوقع من الطلاب الحفاظ على سلوك نموذجي والالتزام بقيم المدرسة وانضباطها في كل الأوقات.",
    rulePromotion: "يجب أن يحصل الطالب على مجموع عام لا يقل عن 50% للترقية إلى الصف التالي. وقد يعيد الطالب الذي يقل عن 50% الصف الحالي حسب لوائح المدرسة وقرار المجلس الأكاديمي.",
    declarationTitle: "إقرار ولي الأمر",
    declarationText: "بالمتابعة، يؤكد ولي الأمر أنه قرأ معلومات القبول، وأن كل المعلومات المقدمة ستكون صحيحة، وأن المتقدم سيلتزم بقواعد ولوائح وسياسات أكاديمية البلاغ الأنجلو عربية.",
    continueNote: "تابع للتحقق من رقم القبول.",
    continueButton: "لقد قرأت هذا، المتابعة إلى الرقم",
  },
});

resources.fr = {
  publicAdmission: {
    ...resources.en.publicAdmission,
    language: {
      label: "Langue",
      english: "Anglais",
      french: "Français",
      arabic: "Arabe",
    },
    common: {
      ...resources.en.publicAdmission.common,
      yes: "Oui",
      no: "Non",
      field: "Champ",
      applicant: "Candidat",
      parentGuardian: "Parent/Tuteur",
      schoolAdmissionPortal: "Portail d'admission scolaire",
      admissionApplication: "Demande d'admission",
      admissionPortal: "Portail d'admission",
      admission: "Admission",
      toBeAnnounced: "À annoncer",
    },
    loading: {
      openingTitle: "Ouverture du portail d'admission",
      connecting: "Connexion sécurisée...",
      pleaseWait: "Veuillez patienter...",
      checkingLink: "Vérification de votre lien",
      verifyingAccess: "Vérification de l'accès d'admission...",
      loadingForm: "Chargement du formulaire d'admission",
      gettingDetails: "Préparation des détails du formulaire...",
      validatingAccess: "Validation de l'accès",
      confirmingRequirements: "Confirmation des exigences d'admission...",
      validatingPin: "Validation du code d'admission",
      checkingPinSession: "Vérification de votre session de code...",
      almostReady: "Presque prêt",
      preparingForm: "Préparation de votre formulaire...",
      ready: "Prêt",
      formLoaded: "Formulaire d'admission chargé.",
      publicAdmission: "Admission publique",
    },
    status: {
      autoSaving: "Enregistrement automatique du brouillon...",
      autoSavePaused: "Enregistrement automatique en pause. Utilisez Enregistrer le brouillon pour réessayer.",
      draftSavedAt: "Brouillon enregistré à {{time}}",
      autoSaveEnabled: "Enregistrement automatique activé",
      restoringDraft: "Restauration de votre brouillon de demande...",
      loadingClassOptions: "Chargement des options de classe...",
      continueWhenComplete: "Vous pourrez continuer une fois cette étape terminée.",
    },
    errors: {
      unavailableLink: "Ce formulaire d'admission n'est plus disponible depuis le lien public sélectionné.",
      unableToLoad: "Impossible de charger le formulaire d'admission.",
      noPublishedForm: "Aucun formulaire publié disponible.",
      noSchoolContext: "Impossible de déterminer le contexte de l'école.",
      pinExpired: "La vérification du code d'admission a expiré. Veuillez saisir de nouveau votre code.",
      required: "{{field}} est obligatoire.",
      validPhone: "Entrez un numéro de téléphone valide à 11 chiffres (ex. 08037668169).",
      validEmail: "Entrez une adresse e-mail valide.",
      validNumber: "Entrez un nombre valide.",
      validDate: "Entrez une date valide.",
      minLength: "Minimum {{count}} caractères requis.",
      maxLength: "Maximum {{count}} caractères autorisés.",
      minValue: "La valeur doit être au moins {{value}}.",
      maxValue: "La valeur ne doit pas dépasser {{value}}.",
      dobFuture: "La date de naissance ne peut pas être dans le futur.",
      duplicatePhone: "Les numéros de téléphone doivent être différents dans le formulaire.",
      resizePhoto: "Impossible de redimensionner la photo de passeport sélectionnée.",
      fileType: "Ce type de fichier n'est pas autorisé pour le champ sélectionné.",
      fileTooLarge: "Le fichier doit être de {{limit}} ou moins.",
      uploadFailed: "Échec du téléversement du fichier.",
      waitUploadsSave: "Veuillez attendre la fin de tous les téléversements avant d'enregistrer le brouillon.",
      waitUploadsSubmit: "Veuillez attendre la fin de tous les téléversements avant de soumettre.",
      completeRequired: "Veuillez compléter tous les champs obligatoires et corriger les erreurs indiquées.",
      saveDraftFailed: "Impossible d'enregistrer le brouillon.",
      submitFailed: "Impossible de soumettre la demande.",
      fixHighlighted: "Veuillez corriger les champs indiqués :",
      noSections: "Aucune section visible n'est disponible pour ce formulaire.",
      uploadsInProgress: "Téléversement en cours. Veuillez attendre avant de soumettre.",
      requiredIncomplete: "Certains champs obligatoires sont encore incomplets. Cliquez sur Soumettre pour voir les erreurs exactes.",
      noOptions: "Aucune option configurée pour ce champ.",
    },
    notifications: {
      draftSaved: "Brouillon enregistré. Saisissez de nouveau le même code d'admission pour continuer plus tard.",
      submitted: "Demande soumise avec succès.",
    },
    unavailable: {
      title: "Admission en ligne fermée",
      detail:
        "L'admission en ligne est actuellement fermée. Veuillez contacter l'école pour le formulaire actif ou réessayer plus tard.",
    },
    pin: {
      ...resources.en.publicAdmission.pin,
      requiredTitle: "Code d'admission requis",
      requiredDetail: "Entrez un code d'admission valide pour ouvrir ce formulaire en ligne.",
      goToEntry: "Aller à la saisie du code",
      verified: "Code d'admission vérifié",
      activeSession: "Votre code est actif pour cette session de soumission.",
      usedAfterSubmit: "Il sera marqué comme utilisé après la soumission finale réussie.",
      lockedField: "Ce champ est fixé par le code d'admission qui vous a été délivré.",
    },
    hero: {
      eyebrow: "Demande en ligne",
      detail: "Remplissez le formulaire ci-dessous et soumettez votre demande en ligne.",
      session: "Session : {{session}}",
    },
    submitted: {
      title: "Demande soumise",
      detail: "Votre demande a été reçue. Conservez ce numéro de référence pour le suivi.",
      albalagExamSoon: " La date de l'examen d'entrée sera annoncée prochainement.",
      applicationNo: "N° de demande",
      applicant: "Candidat",
      class: "Classe",
      usedPin: "Code utilisé",
      fillAnother: "Remplir une autre demande",
    },
    exam: {
      eyebrow: "Carte d'examen d'aptitude / de sélection",
      readyTitle: "La carte d'examen est prête",
      notReadyTitle: "La carte d'examen n'est pas encore prête",
      readyDetail: "Téléchargez et imprimez votre carte d'examen. Présentez-la le jour de la sélection.",
      albalagNotReadyDetail: "La date de l'examen d'entrée sera annoncée prochainement. Veuillez conserver votre numéro de demande et revenir après l'annonce de l'école.",
      notReadyDetail: "L'école n'a pas encore fixé la date du test d'aptitude pour ce formulaire. Veuillez revenir plus tard ou attendre le SMS de l'école avant de revenir imprimer votre carte d'examen.",
      screeningDate: "Date de sélection",
      screeningTime: "Heure de sélection",
      noteLabel: "Instructions / note sur le lieu",
      preparing: "Préparation de la carte d'examen...",
      download: "Télécharger / imprimer la carte d'examen",
      nextLabel: "Que faire ensuite",
      nextAlbalag: "Conservez votre numéro de demande. La date de l'examen d'entrée sera annoncée prochainement.",
      nextDefault: "Conservez votre numéro de demande. Revenez sur ce portail après l'annonce de la date du test d'aptitude par l'école.",
    },
    acknowledgement: {
      preparing: "Préparation de l'accusé de réception...",
      download: "Télécharger le formulaire d'accusé de réception",
    },
    guide: {
      eyebrow: "Avant de commencer",
      title: "Lisez le guide de demande",
      detail: "Complétez d'abord les étapes ci-dessous, puis commencez la demande en ligne.",
      estimatedTimeTitle: "Temps estimé",
      estimatedTimeDetail: "La plupart des parents remplissent ce formulaire en 8 à 15 minutes.",
      mobileTitle: "Adapté au mobile",
      mobileDetail: "Utilisez facilement votre téléphone. Vous pouvez continuer section par section.",
      documentsTitle: "Documents requis",
      documentsDetail: "Les photos de passeport sont redimensionnées automatiquement. Les autres fichiers sont limités à 1MB.",
      reviewTitle: "Vérifier avant soumission",
      reviewDetail: "Confirmez les numéros de téléphone et les champs obligatoires pour éviter les retards.",
      checklistTitle: "Liste rapide",
      checklistPhone: "Utilisez des numéros nigérians à 11 chiffres (ex. 08037668169).",
      checklistRequired: "Assurez-vous que tous les champs obligatoires sont remplis avant de soumettre.",
      checklistPassport: "Les photos de passeport peuvent être prises avec l'appareil photo ou sélectionnées depuis les fichiers.",
      checklistApplicationNo: "Conservez votre numéro de demande après la soumission finale.",
      checklistDraft: "Vous pouvez enregistrer un brouillon et continuer plus tard.",
    },
    progress: {
      title: "Progression de la demande",
      completed: "{{percent}}% terminé",
      sectionOf: "Section {{current}} sur {{total}}",
      complete: "Terminé",
      step: "Étape {{step}}",
      required: "Obligatoire :",
    },
    actions: {
      ...resources.en.publicAdmission.actions,
      continueApplication: "Continuer la demande",
      startApplication: "Commencer la demande",
      previous: "Précédent",
      next: "Suivant",
      savingDraft: "Enregistrement du brouillon...",
      updateDraft: "Mettre à jour le brouillon",
      saveDraft: "Enregistrer le brouillon",
      submitting: "Soumission...",
      submitApplication: "Soumettre la demande",
      continue: "Continuer",
    },
    upload: {
      replace: "Remplacer le fichier",
      fileUpload: "Téléversement de fichier",
      passportPreview: "Aperçu de la photo de passeport",
      uploadingPhoto: "Téléversement de la photo redimensionnée...",
      photoReady: "Photo prête.",
      currentFile: "Fichier actuel téléversé",
      viewFile: "Voir le fichier téléversé",
      remove: "Retirer",
      browsePassport: "Choisir la photo de passeport",
      chooseFile: "Choisir un fichier",
      useCamera: "Utiliser l'appareil photo",
      uploading: "Téléversement...",
      passportResizeNote: "Les photos de passeport sont redimensionnées avant le téléversement.",
      maxSize: "Max {{size}}",
      replaceNote: "Sélectionnez un autre fichier ci-dessus pour remplacer le fichier actuel",
    },
    input: {
      selectOption: "Sélectionnez une option",
      agree: "J'accepte",
    },
    meta: {
      description: "Complétez la demande d'admission pour {{schoolName}}.",
    },
    codeEntry: {
      pageLabel: "Code d'admission",
      metaDescription: "Entrez votre code d'admission pour continuer {{formTitle}} à {{schoolName}}.",
      onlineAdmission: "Admission en ligne",
      headerDetail: "Entrez votre code d'admission pour continuer votre demande.",
      pinFormatNote: "Le code contient uniquement des chiffres et se valide automatiquement une fois complet.",
      admissionPin: "Code d'admission",
      formatExample: "Format : 001-679-818-188",
      digitsCount: "{{count}}/12 chiffres",
      validating: "Validation...",
      validatePin: "Valider le code",
      verifiedPin: "Code vérifié : {{code}}",
      existingFound: "Demande existante trouvée",
      existingStatus: "avec le statut",
      activeDraft: "Une demande active en cours a été trouvée pour ce code.",
      downloadOfferLetter: "Télécharger la lettre d'admission / d'offre",
      preparingPdf: "Préparation du PDF...",
      downloadExamCard: "Télécharger la carte d'examen",
      preparingExamCard: "Préparation de la carte d'examen...",
      continueApplication: "Continuer la demande",
      fullPin: "Veuillez saisir votre code d'admission complet à 12 chiffres.",
      completePinBeforeValidate: "Veuillez compléter le code avant la validation.",
      verifiedRedirect: "Code d'admission vérifié. Redirection vers votre demande...",
      pinVerified: "Code d'admission vérifié.",
      admittedPrintLetter: "Code vérifié pour un candidat admis. Vous pouvez maintenant imprimer la lettre d'admission.",
      admittedRecordFound: "Dossier d'admission trouvé pour ce code.",
      linkedStatus: "Ce code est lié à une demande existante avec le statut : {{status}}.",
      existingApplicationFound: "Demande existante trouvée pour ce code.",
      contextUnavailable: "Impossible d'ouvrir la page du code d'admission.",
      noPublishedForm: "Aucun formulaire d'admission publié n'est actuellement disponible pour cette école.",
      pinDifferentForm: "Ce code a été généré pour un autre formulaire d'admission. Entrez le code créé pour ce formulaire.",
      pinAlreadyLinked: "Ce code est déjà lié à une demande soumise. Entrez-le à nouveau pour voir le résultat actuel.",
      pinBlocked: "Ce code est bloqué. Contactez l'école pour obtenir de l'aide.",
      invalidPin: "Code d'admission invalide. Veuillez vérifier et réessayer.",
      blockedPin: "Ce code d'admission est actuellement bloqué.",
      usedPin: "Ce code d'admission a déjà été utilisé.",
      unableValidate: "Impossible de valider le code pour le moment.",
    },
    albalagIntro: {
      eyebrow: "Informations d'admission en ligne",
      title: "AL-BALAG ANGLO ARABIC ACADEMY JOS",
      readBeforePin: "Veuillez lire ces informations avant d'entrer votre code d'admission.",
      greeting: "Assalamu Alaikum wa Rahmatullahi wa Barakatuh.",
      introOne: "Merci de votre intérêt pour l'inscription de votre enfant à Albalag Anglo-Arabic Academy, Jos. L'école propose un enseignement en internat qui combine la mémorisation du Coran, la langue arabe, les études islamiques, les sciences modernes, les compétences professionnelles et les technologies de l'information.",
      introTwo: "Veuillez consulter le prospectus de l'école pour plus d'informations avant de compléter la demande.",
      availableClassTitle: "Classe disponible",
      availableClassValue: "JSS 1 garçons en internat uniquement",
      availableClassNote: "Les candidats retenus seront admis en Junior Secondary School One en internat.",
      interviewTitle: "Axes de l'entretien",
      interviewValue: "I.R.K, sciences, Coran, anglais, mathématiques, I.T. et arabe",
      interviewNote: "La lecture, la mémorisation et les bases académiques peuvent être évaluées pendant l'entretien.",
      languageTitle: "Options linguistiques",
      languageValue: "Arabe, anglais et français",
      languageNote: "L'arabe est la langue principale de communication, l'anglais est largement utilisé et le français est enseigné à un niveau de base.",
      feesTitle: "Résumé des frais scolaires",
      feeFirstTerm: "Frais obligatoires du 1er trimestre",
      feeSecondTerm: "Frais obligatoires du 2e trimestre",
      feeThirdTerm: "Frais obligatoires du 3e trimestre",
      feeRefundable: "Frais remboursables par trimestre",
      feedingNote: "Les frais d'alimentation peuvent être révisés d'un trimestre à l'autre en raison des variations des prix des denrées. Les frais doivent être payés au moins une semaine avant la reprise.",
      documentsTitle: "Documents requis",
      birthCertificate: "Acte de naissance",
      transcript: "Relevé académique / résultat précédent",
      medicalCertificate: "Certificat médical",
      acknowledgementLetter: "Lettre d'accusé de réception signée",
      rulesTitle: "Règles importantes",
      ruleLanguage: "L'arabe est la langue principale de communication au sein de l'école. L'anglais est également largement utilisé, tandis que les élèves reçoivent un enseignement de base en français.",
      ruleOfficialInfo: "Les parents et tuteurs doivent obtenir toutes les informations officielles directement auprès de la direction de l'école.",
      ruleNoStaffArrangements: "Aucun parent ou tuteur ne doit confier des tâches ou conclure des arrangements personnels avec un membre du personnel sauf avec les personnes officiellement autorisées par l'école.",
      rulePayments: "Toutes les transactions financières doivent être effectuées par le compte bancaire officiel de l'école.",
      ruleCommunication: "Toute communication entre les parents et l'école doit passer par le numéro de téléphone officiel de l'école ou par d'autres canaux approuvés.",
      ruleProhibitedItems: "L'introduction de provisions alimentaires ou d'autres articles non autorisés dans l'école est strictement interdite. Tout article interdit trouvé en possession d'un élève sera confisqué.",
      ruleVisits: "Les visites non programmées aux élèves ne sont pas autorisées. Toutes les visites doivent être organisées et approuvées à l'avance.",
      ruleStayInSchool: "Les élèves doivent rester dans l'enceinte de l'école pendant toute la durée du trimestre, sauf autorisation officielle.",
      ruleAttendance: "La présence et la participation à toutes les activités académiques, religieuses et extrascolaires sont obligatoires.",
      ruleBehaviour: "Les élèves doivent avoir un comportement exemplaire et respecter en permanence les valeurs et la discipline de l'école.",
      rulePromotion: "Un élève doit obtenir une moyenne générale minimale de 50% pour passer dans la classe supérieure. Un élève en dessous de 50% peut redoubler la classe actuelle selon le règlement de l'école et la décision du conseil académique.",
      declarationTitle: "Déclaration du parent / tuteur",
      declarationText: "En continuant, le parent ou le tuteur confirme que les informations d'admission ont été lues, que toutes les informations fournies seront exactes et que le candidat respectera les règles, règlements et politiques d'Albalag Anglo-Arabic Academy.",
      continueNote: "Continuer pour valider votre code d'admission.",
      continueButton: "J'ai lu ceci, continuer vers le code",
    },
  },
};

Object.assign(resources.fr.publicAdmission.language, {
  hausa: "Haoussa",
  igbo: "Igbo",
  yoruba: "Yoruba",
});

Object.assign(resources.ha.publicAdmission, {
  codeEntry: resources.en.publicAdmission.codeEntry,
  albalagIntro: resources.en.publicAdmission.albalagIntro,
});

resources.ig = {
  publicAdmission: {
    ...resources.en.publicAdmission,
    language: {
      label: "Asusu",
      english: "Bekee",
      french: "French",
      arabic: "Arabic",
      hausa: "Hausa",
      igbo: "Igbo",
      yoruba: "Yoruba",
    },
  },
};

resources.yo = {
  publicAdmission: {
    ...resources.en.publicAdmission,
    language: {
      label: "Ede",
      english: "Geesi",
      french: "Faranse",
      arabic: "Larubawa",
      hausa: "Hausa",
      igbo: "Igbo",
      yoruba: "Yoruba",
    },
  },
};

const resultCheckerEn = {
  language: resources.en.publicAdmission.language,
  portal: "Edusaf Result Portal",
  title: "Check Your Child's Result Online",
  subtitle: "Enter the School Code and Student ID Number provided by your school to verify the student and download the official result as a PDF.",
  secure: "Secure",
  quick: "Quick",
  pdfResult: "PDF Result",
  firstTime: "First time checking a result?",
  tutorialText: "Watch this short tutorial to learn how to enter the student details, verify the student and download the result.",
  hideTutorial: "Hide Tutorial",
  watchTutorial: "Watch Tutorial",
  openYoutube: "Open on YouTube",
  tutorialTitle: "How to check a student result on Edusaf",
  videoDuration: "Video duration: less than one minute",
  verifyingSchoolCode: "Verifying school code",
  loadingPeriod: "Loading the latest published result period...",
  verifyDetails: "Verify Student Details",
  verifyDetailsHelp: "Enter the information exactly as issued by the school.",
  schoolVerified: "School verified",
  verified: "Verified",
  schoolCodeVerified: "School code verified",
  schoolCode: "School Code",
  schoolCodeHelp: "Enter the complete code provided by your school.",
  schoolCodeComplete: "School code format is complete.",
  schoolCodeProgress: "Complete the school code ({{current}}/{{min}}).",
  studentId: "Student ID Number",
  studentIdHelp: "Enter it exactly as shown on the student ID card or report sheet.",
  resultPeriod: "Result period",
  sessionNotSelected: "Session not selected",
  termNotSelected: "Term not selected",
  hide: "Hide",
  change: "Change",
  session: "Session",
  term: "Term",
  autoPeriodHelp: "The latest available session and term are selected automatically. Change them only when checking an earlier result.",
  checkingResult: "Checking Result...",
  checkResult: "Check Result",
  completeInputs: "Enter a complete School Code and Student ID Number to continue.",
  previewTitle: "Student Result Preview",
  previewHelp: "Confirm the student details before downloading the result.",
  studentFound: "Student Found",
  verifiedStudent: "Verified student",
  student: "Student",
  school: "School",
  studentName: "Student Name",
  class: "Class",
  downloadTitle: "Download Official Result",
  downloadHelp: "The result will be downloaded to your device as a PDF document.",
  preparingResult: "Preparing Result...",
  downloadPdf: "Download Result PDF",
  downloadLockedTitle: "Result download is currently locked",
  downloadLockedFallback: "The student details are available, but the school has not published this term's result for download.",
  downloadLockedButton: "Download Locked Until Publication",
  noResultTitle: "No result selected yet",
  noResultHelp: "Enter the School Code and Student ID Number, then click Check Result. The verified student details will appear here.",
  needHelp: "Need help?",
  needHelpText: "Contact your school if you do not know the School Code or Student ID Number.",
  poweredBy: "Powered by Edusaf",
  unableVerifySchoolCode: "Unable to verify school code right now. Please try again.",
  schoolCodeNotFoundWithCode: "School code not found: {{code}}",
  schoolCodeNotFound: "School code not found.",
  noPublishedHistoryDefaulted: "No published history found. Defaulted to current school term: {{session}} • {{term}}",
  noPublishedResultFound: "No published result found for this school code yet.",
  latestPublishedResult: "Latest published result: {{session}} • {{term}} ({{type}})",
  unableVerifySchoolCodeShort: "Unable to verify school code.",
  resultNotPublishedYet: "Result has not been published yet. Preview is available, but download will be enabled after school publish.",
  classResultNotPublished: "{{className}} result has not been published for this term.",
  thisClass: "this class",
  fullyPaidRestricted: "Result visibility is restricted to fully-paid students for this term.",
  noResultRecord: "No result record found for this student in the selected term.",
  provideCompleteDetails: "Provide complete school code ({{min}}+ chars) and Student ID to continue.",
  unableConnect: "Unable to connect to result checker service.",
  schoolCodeNotFoundPlain: "School code not found",
  recordNotFound: "Record not found",
  studentRecordIncomplete: "Student record is incomplete.",
  schoolRecordNotFound: "School record not found.",
  unableFetchResult: "Unable to fetch result.",
  enterCompleteSchoolCode: "Enter complete school code ({{min}}+ chars), e.g. EDUSAF/PL/001.",
};

const resultCheckerFr = {
  ...resultCheckerEn,
  language: resources.fr.publicAdmission.language,
  portal: "Portail de résultats Edusaf",
  title: "Consultez le résultat de votre enfant en ligne",
  subtitle: "Entrez le code de l'école et le numéro d'identification de l'élève fournis par votre école pour vérifier l'élève et télécharger le résultat officiel en PDF.",
  secure: "Sécurisé",
  quick: "Rapide",
  pdfResult: "Résultat PDF",
  firstTime: "Première consultation d'un résultat ?",
  tutorialText: "Regardez ce court tutoriel pour apprendre à saisir les détails de l'élève, vérifier l'élève et télécharger le résultat.",
  hideTutorial: "Masquer le tutoriel",
  watchTutorial: "Regarder le tutoriel",
  openYoutube: "Ouvrir sur YouTube",
  videoDuration: "Durée de la vidéo : moins d'une minute",
  verifyingSchoolCode: "Vérification du code de l'école",
  loadingPeriod: "Chargement de la dernière période publiée...",
  verifyDetails: "Vérifier les détails de l'élève",
  verifyDetailsHelp: "Entrez les informations exactement comme fournies par l'école.",
  schoolVerified: "École vérifiée",
  schoolCodeVerified: "Code de l'école vérifié",
  schoolCode: "Code de l'école",
  schoolCodeHelp: "Entrez le code complet fourni par votre école.",
  schoolCodeComplete: "Le format du code de l'école est complet.",
  schoolCodeProgress: "Complétez le code de l'école ({{current}}/{{min}}).",
  studentId: "Numéro d'identification de l'élève",
  studentIdHelp: "Entrez-le exactement comme indiqué sur la carte d'élève ou le bulletin.",
  resultPeriod: "Période du résultat",
  sessionNotSelected: "Session non sélectionnée",
  termNotSelected: "Trimestre non sélectionné",
  hide: "Masquer",
  change: "Modifier",
  session: "Session",
  term: "Trimestre",
  autoPeriodHelp: "La dernière session et le dernier trimestre disponibles sont sélectionnés automatiquement. Modifiez-les seulement pour consulter un résultat antérieur.",
  checkingResult: "Vérification du résultat...",
  checkResult: "Consulter le résultat",
  completeInputs: "Entrez un code d'école complet et le numéro d'identification de l'élève pour continuer.",
  previewTitle: "Aperçu du résultat de l'élève",
  previewHelp: "Confirmez les détails de l'élève avant de télécharger le résultat.",
  studentFound: "Élève trouvé",
  verifiedStudent: "Élève vérifié",
  student: "Élève",
  school: "École",
  studentName: "Nom de l'élève",
  class: "Classe",
  downloadTitle: "Télécharger le résultat officiel",
  downloadHelp: "Le résultat sera téléchargé sur votre appareil sous forme de document PDF.",
  preparingResult: "Préparation du résultat...",
  downloadPdf: "Télécharger le résultat PDF",
  downloadLockedTitle: "Le téléchargement du résultat est actuellement verrouillé",
  downloadLockedFallback: "Les détails de l'élève sont disponibles, mais l'école n'a pas publié le résultat de ce trimestre pour téléchargement.",
  downloadLockedButton: "Téléchargement verrouillé jusqu'à publication",
  noResultTitle: "Aucun résultat sélectionné",
  noResultHelp: "Entrez le code de l'école et le numéro d'identification de l'élève, puis cliquez sur Consulter le résultat. Les détails vérifiés apparaîtront ici.",
  needHelp: "Besoin d'aide ?",
  needHelpText: "Contactez votre école si vous ne connaissez pas le code de l'école ou le numéro d'identification de l'élève.",
  poweredBy: "Propulsé par Edusaf",
};

const resultCheckerAr = {
  ...resultCheckerEn,
  language: resources.ar.publicAdmission.language,
  portal: "بوابة نتائج Edusaf",
  title: "تحقق من نتيجة طفلك عبر الإنترنت",
  subtitle: "أدخل رمز المدرسة ورقم الطالب المقدمين من المدرسة للتحقق من الطالب وتنزيل النتيجة الرسمية بصيغة PDF.",
  secure: "آمن",
  quick: "سريع",
  pdfResult: "نتيجة PDF",
  firstTime: "أول مرة تتحقق من نتيجة؟",
  tutorialText: "شاهد هذا الشرح القصير لمعرفة كيفية إدخال بيانات الطالب والتحقق منه وتنزيل النتيجة.",
  hideTutorial: "إخفاء الشرح",
  watchTutorial: "مشاهدة الشرح",
  openYoutube: "فتح في YouTube",
  videoDuration: "مدة الفيديو: أقل من دقيقة",
  verifyingSchoolCode: "جار التحقق من رمز المدرسة",
  loadingPeriod: "جار تحميل أحدث فترة نتائج منشورة...",
  verifyDetails: "تحقق من بيانات الطالب",
  verifyDetailsHelp: "أدخل المعلومات كما أصدرتها المدرسة تماما.",
  schoolVerified: "تم التحقق من المدرسة",
  schoolCodeVerified: "تم التحقق من رمز المدرسة",
  schoolCode: "رمز المدرسة",
  schoolCodeHelp: "أدخل الرمز الكامل الذي قدمته المدرسة.",
  schoolCodeComplete: "اكتمل تنسيق رمز المدرسة.",
  schoolCodeProgress: "أكمل رمز المدرسة ({{current}}/{{min}}).",
  studentId: "رقم الطالب",
  studentIdHelp: "أدخله تماما كما هو موضح في بطاقة الطالب أو كشف النتيجة.",
  resultPeriod: "فترة النتيجة",
  sessionNotSelected: "لم يتم اختيار الجلسة",
  termNotSelected: "لم يتم اختيار الفصل",
  hide: "إخفاء",
  change: "تغيير",
  session: "الجلسة",
  term: "الفصل",
  autoPeriodHelp: "يتم اختيار أحدث جلسة وفصل متاحين تلقائيا. غيرهما فقط عند التحقق من نتيجة سابقة.",
  checkingResult: "جار التحقق من النتيجة...",
  checkResult: "تحقق من النتيجة",
  completeInputs: "أدخل رمز المدرسة كاملا ورقم الطالب للمتابعة.",
  previewTitle: "معاينة نتيجة الطالب",
  previewHelp: "أكد بيانات الطالب قبل تنزيل النتيجة.",
  studentFound: "تم العثور على الطالب",
  verifiedStudent: "طالب مؤكد",
  student: "الطالب",
  school: "المدرسة",
  studentName: "اسم الطالب",
  class: "الصف",
  downloadTitle: "تنزيل النتيجة الرسمية",
  downloadHelp: "سيتم تنزيل النتيجة إلى جهازك كمستند PDF.",
  preparingResult: "تجهيز النتيجة...",
  downloadPdf: "تنزيل نتيجة PDF",
  downloadLockedTitle: "تنزيل النتيجة مقفل حاليا",
  downloadLockedFallback: "بيانات الطالب متاحة، لكن المدرسة لم تنشر نتيجة هذا الفصل للتنزيل.",
  downloadLockedButton: "التنزيل مقفل حتى النشر",
  noResultTitle: "لم يتم اختيار نتيجة بعد",
  noResultHelp: "أدخل رمز المدرسة ورقم الطالب، ثم اضغط تحقق من النتيجة. ستظهر بيانات الطالب المؤكدة هنا.",
  needHelp: "تحتاج إلى مساعدة؟",
  needHelpText: "تواصل مع مدرستك إذا كنت لا تعرف رمز المدرسة أو رقم الطالب.",
  poweredBy: "مدعوم من Edusaf",
};

resources.en.resultChecker = resultCheckerEn;
resources.fr.resultChecker = resultCheckerFr;
resources.ar.resultChecker = resultCheckerAr;
resources.ha.resultChecker = { ...resultCheckerEn, language: resources.ha.publicAdmission.language };
resources.ig.resultChecker = { ...resultCheckerEn, language: resources.ig.publicAdmission.language };
resources.yo.resultChecker = { ...resultCheckerEn, language: resources.yo.publicAdmission.language };

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: "en",
    fallbackLng: "en",
    supportedLngs: ["en", "fr", "ar", "ha", "ig", "yo"],
    ns: ["publicAdmission", "resultChecker"],
    defaultNS: "publicAdmission",
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

export function applyDocumentLanguage(language, { allowRtl = false } = {}) {
  if (typeof document === "undefined") return;
  const lng = String(language || "en").split("-")[0];
  document.documentElement.lang = lng;
  document.documentElement.dir = allowRtl && lng === "ar" ? "rtl" : "ltr";
}

applyDocumentLanguage("en");

export default i18n;
